import { useMemo, useState } from 'react';
import { useAsync, useInterval } from '../../components/common';
import { statusApi } from '../../api/status';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, Chip, Switch, TextField } from '@nyce/admin-kit/ui';
import { EmptyState, TableScroller } from '../../components/common';
import { StatusBadge, bySeverity } from '../../components/status';
import type { DatabaseStatus, EnvironmentStatus, HubStatus } from '../../api/status';
import { Kpi, joinParts, pct, type Issue } from './shared';
import { DbMetricsDialog, formatPercent } from '../databases/DbMetrics';

// Operations: environments (pods / deployments), databases, jobs and GKE clusters from `GET /status`.

export const OPS_REFRESH_MS = 30_000;

/** `GET /status` for callers with pods or db read access, refreshed every 30 s while the tab is visible. */
export function useHubStatus(enabled: boolean) {
  const res = useAsync(() => (enabled ? statusApi.get() : Promise.resolve(null)), [enabled]);
  useInterval(() => void res.reload(), OPS_REFRESH_MS, enabled);
  return res;
}

/** Problems in the operations data: environments down / degraded, databases down or near a limit. */
export function opsIssues(s: HubStatus): Issue[] {
  return [
    ...(s.environments ?? [])
      .filter((e) => ['down', 'error', 'warn'].includes(e.status))
      .map((e) => ({
        key: `env:${e.id}`,
        status: e.status,
        name: e.name,
        what: e.discovered ? `namespace · ${e.clusterName ?? ''}` : e.customer || 'environment',
        detail:
          e.error ??
          joinParts([
            e.deployments.down > 0 &&
              `${e.deployments.down} deployment${e.deployments.down > 1 ? 's' : ''} down`,
            e.pods.failing > 0 && `${e.pods.failing} failing pod${e.pods.failing > 1 ? 's' : ''}`,
            e.pods.notReady > 0 && `${e.pods.notReady} not ready`,
            e.deployments.degraded > 0 && `${e.deployments.degraded} degraded`,
          ]),
        to: `/pods?env=${encodeURIComponent(e.id)}`,
      })),
    ...(s.databases ?? [])
      .filter((d) => d.status === 'down' || d.status === 'warn')
      .map((d) => ({
        key: `db:${d.key}`,
        status: d.status,
        name: d.name,
        what: 'database',
        detail:
          d.error ??
          joinParts([
            d.state !== 'READY' && d.state !== 'RUNNABLE' && d.state.toLowerCase(),
            (pct(d.connections, d.maxConnections) ?? 0) >= 80 &&
              `${pct(d.connections, d.maxConnections)} % of connections`,
            (d.cpu ?? 0) >= 0.9 && `CPU ${Math.round((d.cpu ?? 0) * 100)} %`,
          ]),
        to: d.serverId ? `/databases?server=${encodeURIComponent(d.serverId)}` : null,
      })),
    ...(s.jobs?.recentFailures ?? []).map((j) => ({
      key: `job:${j.id}`,
      status: 'warn',
      name: `Job #${j.id} ${j.kind}`,
      what: `${j.serverId}/${j.database}`,
      detail: j.error ?? 'failed',
      to: `/databases?tab=jobs&job=${j.id}`,
    })),
  ];
}

/** Headline numbers of the operations data. */
export function opsNumbers(s: HubStatus) {
  const envs = s.environments ?? [];
  const sum = (f: (e: EnvironmentStatus) => number) => envs.reduce((a, e) => a + f(e), 0);
  const count = <T extends { status: string }>(l: T[], st: string) => l.filter((x) => x.status === st).length;
  const dbs = (s.databases ?? []).filter((d) => d.status !== 'stopped');
  const withLimits = dbs.filter((d) => d.connections != null && d.maxConnections);
  return {
    envs,
    envOk: count(envs, 'ok'),
    envActive: envs.length - count(envs, 'idle'),
    envWarn: count(envs, 'warn'),
    envDown: count(envs, 'down') + count(envs, 'error'),
    podsRunning: sum((e) => e.pods.running),
    podsTotal: sum((e) => e.pods.total - e.pods.completed),
    failing: sum((e) => e.pods.failing),
    notReady: sum((e) => e.pods.notReady),
    depAvailable: sum((e) => e.deployments.available),
    depTotal: sum((e) => e.deployments.total - e.deployments.paused),
    depDown: sum((e) => e.deployments.down),
    depDegraded: sum((e) => e.deployments.degraded),
    dbs,
    dbOk: count(dbs, 'ok'),
    dbWarn: count(dbs, 'warn'),
    dbDown: count(dbs, 'down'),
    conns: withLimits.reduce((a, d) => a + (d.connections ?? 0), 0),
    maxConns: withLimits.reduce((a, d) => a + (d.maxConnections ?? 0), 0),
  };
}

export function OpsKpis({ status: s }: { status: HubStatus }) {
  const n = opsNumbers(s);
  return (
    <div className="nyk-kpis" data-testid="kpis">
      {s.environments && (
        <>
          <Kpi
            label="Environments"
            value={`${n.envOk} / ${n.envActive}`}
            sub={joinParts([
              'healthy',
              n.envWarn > 0 && `${n.envWarn} degraded`,
              n.envDown > 0 && `${n.envDown} down`,
            ])}
            tone={n.envDown > 0 ? 'red' : n.envWarn > 0 ? 'amber' : undefined}
          />
          <Kpi
            label="Pods"
            value={`${n.podsRunning} / ${n.podsTotal}`}
            sub={joinParts([
              'running',
              n.failing > 0 && `${n.failing} failing`,
              n.notReady > 0 && `${n.notReady} not ready`,
            ])}
            tone={n.failing > 0 ? 'red' : n.notReady > 0 ? 'amber' : undefined}
          />
          <Kpi
            label="Deployments"
            value={`${n.depAvailable} / ${n.depTotal}`}
            sub={joinParts([
              'available',
              n.depDegraded > 0 && `${n.depDegraded} degraded`,
              n.depDown > 0 && `${n.depDown} down`,
            ])}
            tone={n.depDown > 0 ? 'red' : n.depDegraded > 0 ? 'amber' : undefined}
          />
        </>
      )}
      {s.databases && (
        <>
          <Kpi
            label="Databases"
            value={`${n.dbOk} / ${n.dbs.length}`}
            sub={joinParts([
              'healthy',
              n.dbWarn > 0 && `${n.dbWarn} degraded`,
              n.dbDown > 0 && `${n.dbDown} down`,
            ])}
            tone={n.dbDown > 0 ? 'red' : n.dbWarn > 0 ? 'amber' : undefined}
          />
          <Kpi
            label="DB connections"
            value={n.maxConns ? n.conns.toLocaleString() : '—'}
            sub={n.maxConns ? `of ${n.maxConns.toLocaleString()} (${pct(n.conns, n.maxConns)} %)` : undefined}
          />
        </>
      )}
      {s.jobs && (
        <Kpi
          label="Jobs · 24 h"
          value={s.jobs.failed > 0 ? `${s.jobs.failed} failed` : `${s.jobs.succeeded} ok`}
          sub={joinParts([
            s.jobs.failed > 0 && `${s.jobs.succeeded} succeeded`,
            s.jobs.running > 0 && `${s.jobs.running} running`,
            s.jobs.queued > 0 && `${s.jobs.queued} queued`,
          ])}
          tone={s.jobs.failed > 0 ? 'amber' : undefined}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------- environments

const SEGMENTS: { key: keyof EnvironmentStatus['pods']; label: string }[] = [
  { key: 'running', label: 'running' },
  { key: 'pending', label: 'starting' },
  { key: 'notReady', label: 'not ready' },
  { key: 'failing', label: 'failing' },
];

function PodBar({ pods }: { pods: EnvironmentStatus['pods'] }) {
  const total = SEGMENTS.reduce((a, s) => a + pods[s.key], 0);
  if (!total) return <div className="oph-podbar oph-podbar-empty" aria-hidden="true" />;
  return (
    <div
      className="oph-podbar"
      role="img"
      aria-label={SEGMENTS.filter((s) => pods[s.key])
        .map((s) => `${pods[s.key]} ${s.label}`)
        .join(', ')}
    >
      {SEGMENTS.filter((s) => pods[s.key]).map((s) => (
        <span
          key={s.key}
          className={`oph-podbar-${s.key}`}
          style={{ flexGrow: pods[s.key] }}
          title={`${pods[s.key]} ${s.label}`}
        />
      ))}
    </div>
  );
}

export function Environments({ envs }: { envs: EnvironmentStatus[] }) {
  const [params, setParams] = useSearchParams();
  const onlyProblems = params.get('show') === 'problems';
  const withIdle = params.get('idle') === '1';
  const idleCount = envs.filter((e) => e.status === 'idle').length;
  const q = (params.get('q') ?? '').trim().toLowerCase();
  const set = (k: string, v: string | null) =>
    setParams(
      (cur) => {
        const next = new URLSearchParams(cur);
        if (v) next.set(k, v);
        else next.delete(k);
        return next;
      },
      { replace: true }
    );
  const shown = useMemo(
    () =>
      envs
        .filter((e) => !onlyProblems || ['down', 'error', 'warn'].includes(e.status))
        .filter((e) => withIdle || e.status !== 'idle')
        .filter(
          (e) =>
            !q ||
            [e.name, e.customer, e.clusterName ?? '', ...e.namespaces].some((t) =>
              t.toLowerCase().includes(q)
            )
        )
        .sort(bySeverity),
    [envs, onlyProblems, withIdle, q]
  );
  return (
    <section className="oph-dash-section" aria-labelledby="dash-envs">
      <div className="oph-dash-section-head">
        <h2 id="dash-envs">
          Environments <span className="nyk-ui-hint">{envs.length}</span>
        </h2>
        <div className="nyk-actions">
          <TextField
            aria-label="Filter environments"
            className="nyk-search"
            placeholder="Filter…"
            value={params.get('q') ?? ''}
            onChange={(e) => set('q', e.target.value || null)}
          />
          <Switch
            checked={onlyProblems}
            onChange={(on: boolean) => set('show', on ? 'problems' : null)}
            label="Only problems"
          />
          {idleCount > 0 && (
            <Switch
              checked={withIdle}
              onChange={(on: boolean) => set('idle', on ? '1' : null)}
              label={`Scaled to 0 (${idleCount})`}
            />
          )}
        </div>
      </div>
      {!shown.length ? (
        <EmptyState card title={envs.length ? 'No environment matches' : 'No environments'} />
      ) : (
        <div className="oph-env-grid" data-testid="env-grid">
          {shown.map((e) => (
            <Link
              key={e.id}
              to={`/pods?env=${encodeURIComponent(e.id)}`}
              className="nyk-card oph-env-card"
              data-env={e.id}
              data-status={e.status}
            >
              <div className="oph-env-card-head">
                <span className="oph-env-card-title">
                  <strong>{e.name}</strong>
                  <span className="nyk-ui-hint">
                    {e.discovered
                      ? `namespace · ${e.clusterName ?? e.clusterId}`
                      : e.customer || e.clusterName}
                  </span>
                </span>
                <StatusBadge status={e.status} />
              </div>
              <PodBar pods={e.pods} />
              <div className="oph-env-card-facts">
                <span>
                  <b>{e.pods.running}</b>/{e.pods.total - e.pods.completed} pods
                </span>
                <span>
                  <b>{e.deployments.available}</b>/{e.deployments.total - e.deployments.paused} deployments
                </span>
                {e.restarts > 0 && <span>{e.restarts} restarts</span>}
                <Chip size="sm" className={`oph-env oph-env-${e.kind}`}>
                  {e.kind}
                </Chip>
              </div>
              {e.error && <div className="oph-env-card-error">{e.error}</div>}
              {e.problems.length > 0 && (
                <ul className="oph-env-card-problems">
                  {e.problems.slice(0, 3).map((p) => (
                    <li key={`${p.namespace}/${p.name}`}>
                      <span className="nyk-mono">{p.name}</span>
                      <span className="oph-env-card-problem">
                        {p.status}
                        {p.restarts > 0 && ` · ${p.restarts}×`}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

// ------------------------------------------------------------------------------------------------------- databases

function Meter({ value, max, label }: { value: number | null; max: number | null; label: string }) {
  const p = pct(value, max);
  if (value == null) return <span className="nyk-ui-hint">—</span>;
  return (
    <span className="oph-meter" title={label}>
      <span className="oph-meter-text">
        {value.toLocaleString()}
        {max ? <span className="nyk-ui-hint"> / {max.toLocaleString()}</span> : null}
      </span>
      {p != null && (
        <span className="oph-meter-track" aria-hidden="true">
          <span
            className={`oph-meter-fill${p >= 90 ? ' oph-meter-red' : p >= 80 ? ' oph-meter-amber' : ''}`}
            style={{ width: `${Math.min(100, p)}%` }}
          />
        </span>
      )}
    </span>
  );
}

const KIND_LABEL: Record<DatabaseStatus['kind'], string> = {
  alloydb: 'AlloyDB',
  cloudsql: 'Cloud SQL',
  registered: 'Registered',
};

export function Databases({ dbs, isAdmin }: { dbs: DatabaseStatus[]; isAdmin: boolean }) {
  const list = [...dbs].sort(bySeverity);
  const [metricsFor, setMetricsFor] = useState<DatabaseStatus | null>(null);
  return (
    <section className="oph-dash-section" aria-labelledby="dash-dbs">
      <div className="oph-dash-section-head">
        <h2 id="dash-dbs">
          Databases <span className="nyk-ui-hint">{dbs.length}</span>
        </h2>
      </div>
      {!list.length ? (
        <EmptyState
          card
          title="No database instances"
          hint="Cloud SQL and AlloyDB instances in the projects the hub can see show up here."
        />
      ) : (
        <div className="nyk-card">
          <TableScroller>
            <table className="nyk-ui-table nyk-cards" data-testid="db-status">
              <thead>
                <tr>
                  <th>Instance</th>
                  <th>Status</th>
                  <th>Engine</th>
                  <th>Connections</th>
                  <th className="nyk-ui-num">CPU</th>
                  <th>DB tools</th>
                </tr>
              </thead>
              <tbody>
                {list.map((d) => (
                  <tr key={d.key} data-db={d.key}>
                    <td className="nyk-card-title">
                      <b>{d.name}</b>
                      <div className="nyk-ui-hint">
                        {joinParts([KIND_LABEL[d.kind], d.role !== 'primary' && d.role, d.projectId, d.tier])}
                      </div>
                      {d.error && <div className="oph-env-card-error">{d.error}</div>}
                    </td>
                    <td data-label="Status">
                      <StatusBadge status={d.status} />
                    </td>
                    <td data-label="Engine">
                      {d.engine} {d.version}
                    </td>
                    <td data-label="Connections">
                      <Meter
                        value={d.connections}
                        max={d.maxConnections}
                        label="Client connections / limit"
                      />
                    </td>
                    <td data-label="CPU" className="nyk-ui-num">
                      {d.kind !== 'registered' && d.resourceName ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={`Metrics of ${d.name}`}
                          title="CPU, memory and connections over time"
                          onClick={() => setMetricsFor(d)}
                        >
                          {d.cpu != null ? formatPercent(d.cpu) : 'Metrics'}
                        </Button>
                      ) : d.cpu != null ? (
                        formatPercent(d.cpu)
                      ) : (
                        '—'
                      )}
                    </td>
                    <td data-label="DB tools">
                      {d.serverId ? (
                        <Link to={`/databases?server=${encodeURIComponent(d.serverId)}`}>{d.serverName}</Link>
                      ) : d.engine !== 'postgres' ? (
                        <span className="nyk-ui-hint">not Postgres</span>
                      ) : isAdmin ? (
                        <Link to={`/admin/db-servers?add=${encodeURIComponent(d.resourceName ?? '')}`}>
                          Add credentials
                        </Link>
                      ) : (
                        <span className="nyk-ui-hint">no credentials</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroller>
        </div>
      )}
      {metricsFor && (
        <DbMetricsDialog
          instance={metricsFor.resourceName!}
          name={metricsFor.name}
          onClose={() => setMetricsFor(null)}
        />
      )}
    </section>
  );
}

// ------------------------------------------------------------------------------------------------- jobs, clusters

export function Jobs({ jobs }: { jobs: NonNullable<HubStatus['jobs']> }) {
  return (
    <section className="oph-dash-section" aria-labelledby="dash-jobs">
      <div className="oph-dash-section-head">
        <h2 id="dash-jobs">Backup and copy jobs · 24 h</h2>
        <Link to="/databases?tab=jobs" className="nyk-ui-hint">
          All jobs
        </Link>
      </div>
      <div className="nyk-card">
        <div className="nyk-chips">
          <Chip size="sm" className="nyk-tone-green">
            {jobs.succeeded} succeeded
          </Chip>
          <Chip size="sm" className={jobs.failed ? 'nyk-tone-red' : 'nyk-tone-grey'}>
            {jobs.failed} failed
          </Chip>
          <Chip size="sm" className="nyk-tone-grey">
            {jobs.running} running
          </Chip>
          <Chip size="sm" className="nyk-tone-grey">
            {jobs.queued} queued
          </Chip>
        </div>
        {jobs.recentFailures.length > 0 && (
          <ul className="oph-attention oph-dash-failures">
            {jobs.recentFailures.map((j) => (
              <li key={j.id}>
                <StatusBadge status="down" />
                <span className="oph-attention-name">
                  <Link to={`/databases?tab=jobs&job=${j.id}`}>
                    #{j.id} {j.kind}
                  </Link>
                  <span className="nyk-ui-hint">
                    {' '}
                    {j.serverId}/{j.database}
                  </span>
                </span>
                <span className="oph-attention-detail">{j.error}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

export function Clusters({ status: s }: { status: HubStatus }) {
  const clusters = s.clusters ?? [];
  return (
    <section className="oph-dash-section" aria-labelledby="dash-clusters">
      <div className="oph-dash-section-head">
        <h2 id="dash-clusters">GKE clusters</h2>
        {s.discovery && (
          <span className="nyk-ui-hint">
            {s.discovery.projects} GCP projects scanned
            {s.discovery.errors > 0 && ` · ${s.discovery.errors} discovery errors`}
          </span>
        )}
      </div>
      <div className="nyk-card">
        <TableScroller>
          <table className="nyk-ui-table nyk-cards" data-testid="cluster-status">
            <thead>
              <tr>
                <th>Cluster</th>
                <th>State</th>
                <th>Version</th>
                <th className="nyk-ui-num">Nodes</th>
              </tr>
            </thead>
            <tbody>
              {clusters.map((c) => (
                <tr key={c.id ?? `${c.projectId}/${c.name}`}>
                  <td className="nyk-card-title">
                    <b>{c.name}</b>
                    <div className="nyk-ui-hint">
                      {c.projectId} · {c.location}
                    </div>
                    {c.error && <div className="oph-env-card-error">{c.error}</div>}
                  </td>
                  <td data-label="State">
                    <StatusBadge
                      status={
                        !c.enabled
                          ? 'stopped'
                          : c.error
                            ? 'error'
                            : c.state === 'RUNNING'
                              ? 'ok'
                              : c.state === 'UNKNOWN'
                                ? 'unknown'
                                : 'warn'
                      }
                    />
                  </td>
                  <td data-label="Version" className="nyk-mono">
                    {c.version ?? '—'}
                  </td>
                  <td data-label="Nodes" className="nyk-ui-num">
                    {c.nodes ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroller>
      </div>
    </section>
  );
}
